import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Input,
  Message,
  SelectPicker,
  TagPicker,
  useToaster,
} from 'rsuite';

import * as requestsApi from '../../api/requests';
import { emptyf } from '../../common/common';
import DynamicTextArea from '../../common/DynamicTextArea';
import {
  FormField,
  FormGrid,
  FormModal,
  FormSection,
} from '../../common/FormModal';
import { useFormErrors } from '../../common/useFormErrors';
import { useConfirm } from '../../common/useConfirm';

type Props = {
  obj: any | null;
  setObj: Dispatch<SetStateAction<any | null>>;
  coursesData: { value: string; label: string }[];
  typesData: { value: string; label: string }[];
  statusesData: { value: number; label: string }[];
  callback?: () => void;
};

type RequestFormState = {
  type: string;
  status: number | null;
  email: string;
  firstName: string;
  secondName: string;
  phone: string;
  childFullName: string;
  age: string;
  courseIds: string[];
  data: string;
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+?[0-9\s-]{10,15}$/;

const normalizeState = (state: RequestFormState): RequestFormState => ({
  ...state,
  email: state.email.trim(),
  firstName: state.firstName.trim(),
  secondName: state.secondName.trim(),
  phone: state.phone.trim(),
  childFullName: state.childFullName.trim(),
  age: state.age.trim(),
  courseIds: [...state.courseIds].sort(),
  data: state.data.trim(),
});

const validateState = (state: RequestFormState) => {
  const errors: Partial<Record<keyof RequestFormState, string>> = {};

  if (!state.type) {
    errors.type = 'Оберіть тип анкети';
  }
  if (state.status === null || state.status === undefined) {
    errors.status = 'Оберіть статус анкети';
  }
  if (!state.email.trim()) {
    errors.email = 'Вкажіть пошту';
  } else if (!emailRegex.test(state.email.trim())) {
    errors.email = 'Невалідна пошта';
  }
  if (!state.firstName.trim()) {
    errors.firstName = "Вкажіть ім'я";
  }
  if (!state.secondName.trim()) {
    errors.secondName = 'Вкажіть прізвище';
  }
  if (!state.phone.trim()) {
    errors.phone = 'Вкажіть телефон';
  } else if (!phoneRegex.test(state.phone.trim())) {
    errors.phone = 'Формат телефону: +380XXXXXXXXX';
  }
  if (state.age) {
    const numericAge = Number(state.age);
    if (Number.isNaN(numericAge)) {
      errors.age = 'Вік має бути числом';
    } else if (numericAge < 1 || numericAge > 120) {
      errors.age = 'Вік має бути від 1 до 120';
    }
  }

  return errors;
};

const normalizePhone = (phone: string) =>
  phone.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');

export default function EditRequestModal({
  obj,
  setObj,
  coursesData,
  typesData,
  statusesData,
  callback = emptyf,
}: Props) {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [formState, setFormState] = useState<RequestFormState>({
    type: '',
    status: null,
    email: '',
    firstName: '',
    secondName: '',
    phone: '',
    childFullName: '',
    age: '',
    courseIds: [],
    data: '',
  });
  const [initialState, setInitialState] = useState<RequestFormState | null>(
    null
  );
  const [showErrors, setShowErrors] = useState(false);
  const {
    errorSummary: apiErrors,
    setApiError,
    clearErrors: clearApiErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити анкету. Спробуйте пізніше',
    resetOn: [obj?.id],
  });
  const [saving, setSaving] = useState(false);

  const mapRequestToState = React.useCallback(
    (request: any | null): RequestFormState => {
      const fallbackType = typesData[0]?.value || '';
      const fallbackStatus =
        statusesData[0]?.value !== undefined ? statusesData[0].value : null;

      if (!request) {
        return {
          type: fallbackType,
          status: fallbackStatus,
          email: '',
          firstName: '',
          secondName: '',
          phone: '',
          childFullName: '',
          age: '',
          courseIds: [],
          data: '',
        };
      }

      return {
        type: request.type || fallbackType,
        status:
          typeof request.status === 'number' ? request.status : fallbackStatus,
        email: request.email || '',
        firstName: request.firstName || '',
        secondName: request.secondName || '',
        phone: request.phone || '',
        childFullName: request.childFullName || '',
        age: request.age === 0 || request.age ? request.age.toString() : '',
        courseIds: request.courses?.map((course: any) => course.id) || [],
        data: request.data || '',
      };
    },
    [statusesData, typesData]
  );

  useEffect(() => {
    const mappedState = mapRequestToState(obj);
    setFormState(mappedState);
    setInitialState(mappedState);
    setShowErrors(false);
    clearApiErrors();
  }, [mapRequestToState, obj]);

  const errors = useMemo(() => validateState(formState), [formState]);
  const errorSummary = [
    ...(showErrors ? Object.values(errors) : []),
    ...apiErrors,
  ];

  const isDirty = useMemo(() => {
    if (!initialState) {
      return false;
    }
    const normalizedCurrent = normalizeState(formState);
    const normalizedInitial = normalizeState(initialState);
    return (
      JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial)
    );
  }, [formState, initialState]);

  const handleClose = async () => {
    if (isDirty) {
      const confirmClose = await confirm({
        message: 'Є незбережені зміни. Закрити без збереження?',
        confirmText: 'Закрити',
      });
      if (!confirmClose) {
        return;
      }
    }
    setObj(null);
  };

  const handleSubmit = async (stayOpen = false) => {
    setShowErrors(true);
    clearApiErrors();

    const validationErrors = validateState(formState);
    if (Object.keys(validationErrors).length > 0 || !obj) {
      return;
    }

    const cleanedState = normalizeState(formState);
    const payload = {
      ...obj,
      type: cleanedState.type,
      status: cleanedState.status,
      email: cleanedState.email,
      firstName: cleanedState.firstName,
      secondName: cleanedState.secondName,
      phone: cleanedState.phone,
      childFullName: cleanedState.childFullName || null,
      age: cleanedState.age ? Number(cleanedState.age) : null,
      data: cleanedState.data || null,
      courseIds: cleanedState.courseIds,
    };

    try {
      setSaving(true);
      await requestsApi.update(obj.id, payload);
      toaster.push(
        <Message type="success" showIcon closable>
          Анкету оновлено
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      callback();

      if (stayOpen) {
        setFormState(cleanedState);
        setInitialState(cleanedState);
        setShowErrors(false);
        return;
      }

      setObj(null);
    } catch (e: any) {
      console.error(e);
      const errorMessage = setApiError(e)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={!!obj}
        onClose={handleClose}
        title="Редагувати анкету"
        errorSummary={errorSummary}
        footer={
          <>
            <Button
              appearance="primary"
              onClick={() => handleSubmit(false)}
              loading={saving}
            >
              Зберегти
            </Button>
            <Button
              appearance="ghost"
              onClick={() => handleSubmit(true)}
              loading={saving}
            >
              Зберегти й залишитися
            </Button>
            <Button onClick={handleClose} appearance="subtle" disabled={saving}>
              Скасувати
            </Button>
          </>
        }
      >
        <FormSection title="Базові дані">
          <FormGrid>
            <FormField
              label="Тип"
              id="request-type"
              required
              error={showErrors ? errors.type : undefined}
            >
              <SelectPicker
                block
                searchable
                cleanable={false}
                data={typesData}
                value={formState.type}
                placeholder="Оберіть тип"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    type: value || '',
                  }))
                }
              />
            </FormField>
            <FormField
              label="Статус"
              id="request-status"
              required
              error={showErrors ? errors.status : undefined}
            >
              <SelectPicker
                block
                searchable
                cleanable={false}
                data={statusesData}
                value={formState.status}
                placeholder="Оберіть статус"
                onChange={(value: number | null) =>
                  setFormState(prev => ({
                    ...prev,
                    status: value ?? null,
                  }))
                }
              />
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Контактні дані">
          <FormGrid>
            <FormField
              label="Пошта"
              id="request-email"
              required
              error={showErrors ? errors.email : undefined}
            >
              <Input
                type="email"
                value={formState.email}
                placeholder="name@domain.com"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    email: value,
                  }))
                }
              />
            </FormField>
            <FormField
              label="Телефон"
              id="request-phone"
              required
              error={showErrors ? errors.phone : undefined}
            >
              <Input
                value={formState.phone}
                placeholder="+380XXXXXXXXX"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    phone: normalizePhone(value),
                  }))
                }
              />
            </FormField>
          </FormGrid>
          <FormGrid>
            <FormField
              label="Ім'я"
              id="request-first-name"
              required
              error={showErrors ? errors.firstName : undefined}
            >
              <Input
                value={formState.firstName}
                placeholder="Ім'я"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    firstName: value,
                  }))
                }
              />
            </FormField>
            <FormField
              label="Прізвище"
              id="request-second-name"
              required
              error={showErrors ? errors.secondName : undefined}
            >
              <Input
                value={formState.secondName}
                placeholder="Прізвище"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    secondName: value,
                  }))
                }
              />
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Дані дитини та курси">
          <FormGrid>
            <FormField label="ПІБ дитини" id="request-child-full-name">
              <DynamicTextArea
                minRows={2}
                maxRows={4}
                placeholder="ПІБ дитини"
                value={formState.childFullName}
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    childFullName: value,
                  }))
                }
              />
            </FormField>
            <FormField
              label="Вік"
              id="request-age"
              error={showErrors ? errors.age : undefined}
            >
              <Input
                type="number"
                value={formState.age}
                placeholder="Вік"
                onChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    age: value,
                  }))
                }
                min={1}
                max={120}
              />
            </FormField>
          </FormGrid>
          <FormGrid>
            <FormField label="Курси" id="request-courses">
              <TagPicker
                block
                data={coursesData}
                value={formState.courseIds}
                placeholder="Курси"
                onChange={(value: string[]) =>
                  setFormState(prev => ({
                    ...prev,
                    courseIds: value || [],
                  }))
                }
              />
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Додаткова інформація">
          <FormField label="Коментар" id="request-comment">
            <DynamicTextArea
              minRows={3}
              maxRows={6}
              placeholder="Коментар"
              value={formState.data}
              onChange={value =>
                setFormState(prev => ({
                  ...prev,
                  data: value,
                }))
              }
            />
          </FormField>
        </FormSection>
      </FormModal>
    </>
  );
}
